import { useMemo } from 'react'

import useChartDataShape from 'src/hooks/composite/calculation/useChartDataShape'
import useExtendedTotalSpend from 'src/hooks/composite/calculation/useExtendedTotalSpend'
import useBudgets from 'src/hooks/services/microsoft/queries/useBudgets'
import useDailyTotalsForTagset from 'src/hooks/services/microsoft/queries/useDailyTotalsForTagset'
import { useTenantsQuery } from 'src/hooks/services/servicenow/queries/useTenantsQuery'

import Button from 'src/components/Common/Button'
import ForecastLineGraph from 'src/components/Graphs/ForecastLineGraph'

import moment, { type Moment } from 'moment'
import { useNavigate } from 'react-router-dom'

import { Tile, TileContent, TileHeader } from '../Tile'

const AzureTile = () => {
  const navigate = useNavigate()
  const dateRange = [moment().startOf('month'), moment().endOf('month')] as [Moment, Moment]

  const { data: tenants } = useTenantsQuery({ active: true })
  const selectedTenants = tenants
  const selectedSubscriptions = useMemo(() => tenants.flatMap((t) => t.subscriptions), [tenants])

  const budgets = useBudgets({ tenants })

  const budgetTotal = useMemo(() => {
    const filtered = budgets?.filter((b) => tenants.some((t) => t.id === b.tenant_id))
    const total = filtered?.reduce((acc, cur) => acc + cur.value, 0)
    return total
  }, [budgets, tenants])

  const { data: generalConsumption, isLoading: isConsumptionLoading, enabled: isConsumptionEnabled } = useDailyTotalsForTagset({ dateRange, tenants, tags: [] })

  const [tenantExtendedTotalSpend, tenantCompleteDataSetStats] = useExtendedTotalSpend({
    data: generalConsumption,
    stack: 'tenant',
    selectedSubscriptions,
    selectedTenants
  })

  const { data: alteredConsumption, periodicBudget } = useChartDataShape({
    data: tenantExtendedTotalSpend ?? [],
    budgetTotal,
    dateRange,
    cumulative: true,
    completeDataSetStats: tenantCompleteDataSetStats
  })

  return (
    <Tile className="select-none">
      <TileHeader>
        <TileHeader.Title>Azure Spend</TileHeader.Title>
        <TileHeader.Subtitle>{moment().format('MMMM YYYY')}</TileHeader.Subtitle>
        <TileHeader.Item>
          <Button bordered className="duration-100 hover:bg-th-content-secondary" onMouseDown={() => navigate('/azure_overview')}>
            View Details
          </Button>
        </TileHeader.Item>
      </TileHeader>

      <TileContent>
        <ForecastLineGraph
          data={alteredConsumption}
          budget={periodicBudget}
          cumulative
          interactive
          isLoading={isConsumptionLoading && isConsumptionEnabled}
          className="h-[196px] !p-0"
        />
      </TileContent>
    </Tile>
  )
}

export default AzureTile
